import { Tooltip } from '@mui/material'
import React, { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import PriorityTag from './PriorityTag'

type PriorityTagsProps = {
  priorities: SystemSettingPriorityLevel[]
  selectedPriorityLevels: number[]
  onToggle?: (level: number) => void
}

export default function PriorityTags({
  priorities,
  selectedPriorityLevels,
  onToggle,
}: PriorityTagsProps): ReactElement {
  const { t } = useTranslation()

  const sortedPriorities = useMemo(
    () => [...priorities].sort((a, b) => a.priority_level - b.priority_level),
    [priorities]
  )

  if (sortedPriorities.length === 0) {
    return <></>
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {sortedPriorities.map((priority) => {
        const isSelected = selectedPriorityLevels.includes(priority.priority_level)

        return (
          <span key={priority.priority_level} className="group relative">
            <PriorityTag
              priority={priority}
              disabled={!isSelected}
              onClick={onToggle ? () => onToggle(priority.priority_level) : undefined}
            />
            {onToggle && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onToggle(priority.priority_level)
                }}
                className={`absolute -top-1.5 -right-1.5 flex items-center justify-center w-4 h-4 p-0 rounded-full text-white transition cursor-pointer opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto ${
                  isSelected ? 'bg-grey-6 hover:bg-grey-7' : 'bg-success-4 hover:bg-success-5'
                }`}
                aria-label={
                  isSelected
                    ? t('Disable {{name}}', { name: t(priority.display_name) })
                    : t('Enable {{name}}', { name: t(priority.display_name) })
                }
              >
                <Tooltip
                  title={
                    isSelected
                      ? t('Hide {{title}}', { title: t(priority.display_name) })
                      : t('Show {{title}}', { title: t(priority.display_name) })
                  }
                  arrow
                  placement="right"
                >
                  <span className="flex w-full h-full items-center justify-center leading-none">
                    <NewIcon
                      icon={isSelected ? 'fas fa-times' : 'fas fa-plus'}
                      color="white"
                      ariaLabel={isSelected ? t('Disable') : t('Enable')}
                      size="extraSmall"
                    />
                  </span>
                </Tooltip>
              </button>
            )}
          </span>
        )
      })}
    </div>
  )
}
